import React from 'react';
import { Container, Card, Navbar } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import VersionFooter from '../components/VersionFooter';

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="privacy-terms-page">
      <Navbar bg="light" expand="lg" className="shadow-sm">
        <Container>
          <Navbar.Brand 
            href="/" 
            onClick={(e) => { e.preventDefault(); navigate('/'); }}
            className="fw-bold fs-4"
            style={{ cursor: 'pointer' }}
          >
            <img src="/logo.svg" alt="trip147" style={{ width: '40px', height: '40px', marginRight: '10px' }} />
            trip147
          </Navbar.Brand>
        </Container>
      </Navbar>
      <Container className="pt-4 pb-5">
        <div className="mb-4">
          <h1 className="display-5 fw-bold mb-3">Terms of Service</h1>
          <p className="text-muted">Last updated: November 2025</p>
        </div>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">1. Acceptance of Terms</h2>
            <p>
              By accessing or using trip147 ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not use the Service.
            </p>
            <p>
              <strong>Cookie Agreement:</strong> By logging into our service, you agree to our use of cookies as described in our Privacy Policy. Cookies are essential for maintaining your login session and providing a secure, personalized experience.
            </p>
            <p className="mb-0">
              These Terms constitute a legally binding agreement between you and trip147. We reserve the right to modify these Terms at any time, and such modifications will be effective immediately upon posting.
            </p>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">2. Beta Service Disclaimer</h2>
            <p><strong>IMPORTANT:</strong> trip147 is currently in beta (testing) mode. This means:</p>
            <ul>
              <li>The service is still under active development</li>
              <li>Features may change, be removed, or added without notice</li>
              <li>The service may experience bugs, errors, or downtime</li>
              <li>Data loss or service interruptions may occur</li>
              <li>No service level agreement (SLA) or uptime guarantee is provided</li>
              <li>The service is provided "as is" without warranties of any kind</li>
            </ul>
            <p className="mt-3 mb-0">
              By using the Service, you acknowledge that you are using a beta version and accept the risks associated with beta software. We recommend keeping backups of important trip data.
            </p>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">3. Account Registration</h2>
            <p>To use the Service, you must:</p>
            <ul>
              <li>Be at least 13 years of age (or the age of majority in your jurisdiction)</li>
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain and update your account information to keep it accurate</li>
              <li>Maintain the security of your account credentials</li>
              <li>Accept responsibility for all activities that occur under your account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>
            <p className="mt-3 mb-0">
              You may not share your account credentials with others or create multiple accounts to circumvent service limitations.
            </p>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">4. Acceptable Use</h2>
            <p>You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree NOT to:</p>
            <ul>
              <li>Use the Service for any illegal or unauthorized purpose</li>
              <li>Violate any laws or regulations in your jurisdiction</li>
              <li>Infringe upon the rights of others</li>
              <li>Transmit any viruses, malware, or harmful code</li>
              <li>Attempt to gain unauthorized access to the Service or its systems</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Use automated systems (bots, scrapers) to access the Service without permission</li>
              <li>Abuse, harass, or harm other users</li>
              <li>Impersonate any person or entity</li>
              <li>Use the Service to transmit spam or unsolicited communications</li>
            </ul>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">5. User Content and Intellectual Property</h2>
            
            <h3 className="h5 mt-3 mb-2">5.1 Your Content</h3>
            <p>You retain all ownership rights to the trip plans, data, and content you create using the Service ("User Content"). By using the Service, you grant us a limited, non-exclusive, worldwide license to:</p>
            <ul>
              <li>Store, process, and display your User Content to provide the Service</li>
              <li>Use your User Content to generate AI-powered recommendations</li>
              <li>Backup and maintain your User Content</li>
            </ul>

            <h3 className="h5 mt-3 mb-2">5.2 Our Content</h3>
            <p>
              The Service, including its design, features, functionality, and all content (except User Content), is owned by trip147 and protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or create derivative works of the Service without our express written permission.
            </p>

            <h3 className="h5 mt-3 mb-2">5.3 Third-Party Content</h3>
            <p>
              The Service may display content from third-party services. This content is subject to the terms and licenses of those third-party providers.
            </p>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">6. AI-Generated Content</h2>
            <p>
              The Service uses artificial intelligence to generate trip suggestions, activity recommendations, and travel information. You acknowledge that:
            </p>
            <ul>
              <li>AI-generated content may contain errors or inaccuracies</li>
              <li>AI-generated content should be verified before making travel decisions</li>
              <li>We are not responsible for the accuracy of AI-generated recommendations</li>
              <li>You should independently verify all travel information (prices, availability, schedules, etc.)</li>
            </ul>
            <p className="mt-3 mb-0">
              <strong>Travel Disclaimer:</strong> The Service provides planning tools and suggestions only. We do not guarantee the accuracy of travel information, prices, availability, or recommendations. Always verify information with official sources before making travel arrangements.
            </p>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">7. Service Availability and Modifications</h2>
            <p>We reserve the right to:</p>
            <ul>
              <li>Modify, suspend, or discontinue the Service at any time without notice</li>
              <li>Change features, functionality, or user interface</li>
              <li>Impose usage limits or restrictions</li>
              <li>Perform maintenance that may temporarily interrupt service</li>
            </ul>
            <p className="mt-3 mb-0">
              We are not liable for any loss or damage resulting from service interruptions, modifications, or discontinuation.
            </p>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">8. Sharing and Public Content</h2>
            <p>
              The Service allows you to share trip plans with others. When you share a plan:
            </p>
            <ul>
              <li>Private information (costs, to-do lists, packing lists, notes, private links) is automatically removed</li>
              <li>Only itinerary information (activities, locations, dates) is shared</li>
              <li>Shared plans are accessible via a unique link</li>
              <li>Shared plans expire automatically after a set period</li>
              <li>You are responsible for who you share your plans with</li>
            </ul>
            <p className="mt-3 mb-0">
              We are not responsible for how others use or distribute your shared content.
            </p>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">9. Limitation of Liability</h2>
            <p><strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</strong></p>
            <ul>
              <li>The Service is provided "AS IS" and "AS AVAILABLE" without warranties of any kind, either express or implied</li>
              <li>We disclaim all warranties, including but not limited to merchantability, fitness for a particular purpose, and non-infringement</li>
              <li>We are not liable for any indirect, incidental, special, consequential, or punitive damages</li>
              <li>We are not liable for any loss of data, profits, or business opportunities</li>
              <li>We are not liable for any travel-related decisions made based on Service content or recommendations</li>
              <li>Our total liability shall not exceed the amount you paid for the Service (currently $0, as the Service is free)</li>
            </ul>
            <p className="mt-3 mb-0">
              Some jurisdictions do not allow the exclusion of certain warranties or limitations of liability, so some of the above limitations may not apply to you.
            </p>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">10. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless trip147, its operators, and affiliates from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from:
            </p>
            <ul>
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of another party</li>
              <li>Your User Content</li>
            </ul>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">11. Account Termination</h2>
            <p>We reserve the right to:</p>
            <ul>
              <li>Suspend or terminate your account at any time, with or without cause or notice</li>
              <li>Delete your account and all associated data if you violate these Terms</li>
              <li>Refuse service to anyone for any reason</li>
            </ul>
            <p className="mt-3">
              You may delete your account at any time through your account settings. Upon account deletion:
            </p>
            <ul>
              <li>Your account information will be permanently deleted</li>
              <li>All your trip plans and data will be permanently removed</li>
              <li>Shared plans will remain accessible until they expire</li>
            </ul>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">12. Data Export and Backup</h2>
            <p>
              The Service provides functionality to export your trip data. We recommend regularly exporting and backing up your important trip data, as we cannot guarantee against data loss, especially during the beta period.
            </p>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">13. Third-Party Services</h2>
            <p>
              The Service integrates with third-party services for various features. Your use of these services is subject to their respective terms and privacy policies. We are not responsible for the practices of third-party services.
            </p>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">14. Changes to Terms</h2>
            <p>
              We may modify these Terms at any time. We will notify you of material changes by posting the updated Terms on this page and updating the "Last updated" date. Your continued use of the Service after such modifications constitutes acceptance of the updated Terms.
            </p>
            <p className="mt-3 mb-0">
              If you do not agree to the modified Terms, you must stop using the Service and delete your account.
            </p>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">15. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of [Your Jurisdiction], without regard to its conflict of law provisions. Any disputes arising from these Terms or your use of the Service shall be resolved in the courts of [Your Jurisdiction].
            </p>
            <p className="mt-3 mb-0">
              <em>Note: Please update this section with your actual jurisdiction.</em>
            </p>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">16. Severability</h2>
            <p>
              If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
            </p>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">17. Entire Agreement</h2>
            <p>
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and trip147 regarding the use of the Service and supersede all prior agreements and understandings.
            </p>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">18. Contact Information</h2>
            <p>
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <p className="mb-0">
              <strong>Email:</strong> gil.trip147@gmail.com<br />
              <strong>Service:</strong> trip147<br />
              <strong>Website:</strong> <a href="https://trip147.com/">https://trip147.com/</a>
            </p>
          </Card.Body>
        </Card>
      </Container>
      <VersionFooter />
    </div>
  );
}
